import type { LiteralUnion } from './literal-union.js';

/** Стабильные свойства точек слоя Автокрафта. Служебные ключи начинаются с `_`. */
export const AUTOCRAFT_GENERATOR_PROPERTY = '_autocraft_generator';
export const AUTOCRAFT_DATA_PROPERTY = '_autocraft_data';
export const AUTOCRAFT_COVER_PROPERTY = 'Макет';
export const AUTOCRAFT_PDF_PROPERTY = 'PDF';
export const AUTOCRAFT_ROTATION_PROPERTY = 'Угол поворота';
/** Какая страница многостраничного макета показывается на карте — выбор точки, а не слоя. */
export const AUTOCRAFT_PAGE_PROPERTY = '_autocraft_page';
/** Сколько страниц было в последнем собранном для этой точки PDF (заполняет сервер). */
export const AUTOCRAFT_PAGE_COUNT_PROPERTY = '_autocraft_page_count';
/**
 * Отпечаток данных, по которым собрана обложка (`autocraftLayoutFingerprint`). По нему видно,
 * что обложка отстала от объекта, на который ссылается поле, — без похода в Автокрафт.
 */
export const AUTOCRAFT_COVER_SIGNATURE_PROPERTY = '_autocraft_cover_sig';
/**
 * Отпечаток «обложка заведомо устарела»: данные стороны переписали снаружи формы (выбрали
 * конечную точку указателя), и собранное раньше им больше не соответствует. Ни с одним
 * настоящим отпечатком не совпадает, поэтому форма стороны пересобирает макет при открытии —
 * в том числе когда обложки ещё нет или она собрана до появления отпечатков.
 */
export const AUTOCRAFT_COVER_SIGNATURE_OUTDATED = 'outdated';

/** Страница макета по умолчанию: Автокрафт нумерует с 1. */
export const AUTOCRAFT_DEFAULT_PREVIEW_PAGE = 1;
/** Верхняя граница номера страницы — защита от опечатки в UI, а не ограничение Автокрафта. */
export const AUTOCRAFT_MAX_PREVIEW_PAGE = 999;

export interface AutocraftGenerator {
  hash: string;
  name: string;
}

export type AutocraftFieldType = 'string' | 'text' | 'select' | 'group' | 'link';

/**
 * Ключ «без картинки» у select'а из папки файлов. Уходит в PDF как есть, подпись
 * (тоже `__none__`) для людей не годится — её подставляет интерфейс.
 */
export const AUTOCRAFT_NONE_OPTION = '__none__';

/** Опция select'а из папки файлов (`PATH` + `pathMediaDirectoryHash`): ключ — URL файла. */
export interface AutocraftFileOption {
  /** `title` файла, иначе `originalName`. */
  name?: string;
}

/**
 * Вариант набора группы-массива (`template.arrayGroup.variants` элемента в Автокрафте). Схема
 * формы по API отдаёт только флаг `hasArrayVariants` — список досыпает наш сервер (см.
 * `withAutocraftArrayVariants`), а если Автокрафт когда-нибудь начнёт отдавать его сам, поле
 * то же.
 */
export interface AutocraftArrayVariant {
  id: string;
  name?: string;
}

/**
 * Суффикс ключа варианта набора: `{hash группы}.__variant[0]`. Пустое значение (или ключа нет) —
 * «обычный» набор без варианта.
 */
export const AUTOCRAFT_ARRAY_VARIANT_SUFFIX = '.__variant';

/**
 * Потолок наборов одной группы-массива. Число наборов считается по индексам ключей в данных, а
 * данные точки пишет редактор: ключ `stop1[999999999]` без потолка развернулся бы в миллиард
 * наборов при сборке макета. Индексы за потолком не считаются набором вовсе.
 */
export const AUTOCRAFT_MAX_ARRAY_SETS = 100;

/**
 * Сколько групп над полем учитывается, когда ищем его наборы (по `parent` схемы и по дереву
 * шаблона). Схему и дерево присылает сервер по ссылке, которую задаёт редактор, а цепочка из
 * тысяч вложенных групп-массивов стоила куба по глубине при каждой сборке значений: каждое поле
 * копирует растущие индексы всех предков. Настоящие макеты вкладывают группы на единицы уровней;
 * предки выше потолка не читаются вовсе.
 */
export const AUTOCRAFT_MAX_NESTING_DEPTH = 32;

/**
 * Свой файл в PATH-фрейме (`allowFileUpload`): что принимает Автокрафт — растр кроме SVG, PDF и
 * AI. Файл кладём в наш бакет, а в данные макета уходит его публичный URL.
 */
export const AUTOCRAFT_UPLOAD_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.pdf', '.ai'] as const;
export const AUTOCRAFT_UPLOAD_MAX_BYTES = 30 * 1024 * 1024;

export interface AutocraftDataField {
  type: LiteralUnion<AutocraftFieldType>;
  label?: string;
  /**
   * Только у `select`: ключ — значение для PDF, значение — подпись. Текстовый список
   * (`TEXT_FRAME`) даёт строку, папка файлов — `{ name }` с URL файла в ключе.
   */
  options?: Record<string, string | AutocraftFileOption>;
  /**
   * Только у select'а из словаря Автокрафта: ключи `options` в порядке строк словаря. Досыпает
   * наш сервер (`withAutocraftDictionaryOptionOrder`): `options` Автокрафт отдаёт из jsonb, где
   * ключи объекта идут по длине, а ключи-числа («58», «110») JSON-объект и сам ставит по
   * возрастанию — поэтому порядок отдельным массивом. Ключ не из массива идёт после всех
   * упорядоченных, в порядке `options`.
   */
  optionsOrder?: string[];
  publicDefaultValue?: string;
  isPublic?: boolean;
  isDeletable?: boolean;
  defaultDrawable?: boolean;
  elementHash?: string;
  elementType?: LiteralUnion<'TEXT_FRAME' | 'PATH' | 'GROUP'>;
  parent?: string | null;
  /** Текст можно оформлять стилями символов: маркеры стиля пишутся прямо в тексте (`**…**`). */
  allowTextStyles?: boolean;
  allowedCharacterStyles?: string[] | null;
  /**
   * PATH: в форму попадает загрузка своего файла. Может стоять и у `isPublic: false` — тогда
   * выбора из списка нет, только загрузка.
   */
  allowFileUpload?: boolean;
  /** PATH + папка медиа: `options` — файлы этой папки. */
  pathMediaDirectoryHash?: string;
  /** Группа-массив: наборы её полей повторяются, ключи данных — с индексами (`hash[0]`). */
  isArrayGroup?: boolean;
  arrayRenderMode?: LiteralUnion<'bySet' | 'byElement'>;
  hasArrayVariants?: boolean;
  /** Варианты набора группы-массива, см. {@link AutocraftArrayVariant}. */
  arrayVariants?: AutocraftArrayVariant[];
  /**
   * Группа-массив: имя записи в заголовке набора («Направление» → «Направление 1»). Считает наш
   * сервер по элементам шаблона, как публичная форма Автокрафта (`resolveArraySetTitleName`):
   * единственный именованный прямой ребёнок группы, иначе единственная именованная группа среди
   * них, иначе имя самой группы. В схеме формы по API его нет: ребёнком бывает группа шаблона,
   * которой в схеме нет.
   */
  arraySetName?: string;
  /**
   * Группа-массив достроена нами (`withImpliedAutocraftArrayGroups`): в схеме Автокрафта её нет,
   * на неё только ссылаются поля набора. Название и варианты досыпает сервер с публичной страницы.
   */
  implied?: boolean;
  /** Поле внутри группы-массива: прототип, который повторяется в каждом наборе. */
  isArrayPrototypeField?: boolean;
  /** Ближайшая группа-массив прототипа. */
  arrayGroupHash?: string;
  /** Ключ поля в `dataItems` без индексов; с индексами — `arraySourceHash[0]`. */
  arraySourceHash?: string;
  variableId?: string;
  variableKey?: string;
  required?: boolean;
}

export interface AutocraftDataModel {
  _order?: string[];
  [fieldKey: string]: AutocraftDataField | string[] | undefined;
}

export interface AutocraftFormItem {
  id?: string;
  /** `break` — следующее поле с новой строки сетки, даже если в этой ещё есть место. */
  type: LiteralUnion<'field' | 'heading' | 'separator' | 'break'>;
  fieldKey?: string;
  width?: LiteralUnion<'full' | 'half' | 'third' | 'quarter'>;
  text?: string;
  /** Только у select: выпадающий список или ряд кнопок. */
  selectDisplayMode?: LiteralUnion<'list' | 'tabs'>;
  /** Поле без подписи (подпись уходит в placeholder и aria-label). */
  hideLabel?: boolean;
}

/** Как показать выбор варианта набора у группы-массива (`dataForm.arrayVariantSelect[hash]`). */
export interface AutocraftArrayVariantSelectSettings {
  selectDisplayMode?: LiteralUnion<'list' | 'tabs'>;
  /** Подпись пустого значения, если не дефолтное «Нет». */
  variantEmptyLabel?: string;
  /** Без подписи «Вариант». */
  hideLabel?: boolean;
}

export interface AutocraftDataForm {
  version?: number;
  formWidth?: LiteralUnion<'compact' | 'standard' | 'wide'>;
  items?: AutocraftFormItem[];
  arrayVariantSelect?: Record<string, AutocraftArrayVariantSelectSettings>;
}

export interface AutocraftGeneratorSchema {
  success: boolean;
  generator: AutocraftGenerator;
  dataModel: AutocraftDataModel;
  dataForm?: AutocraftDataForm;
}

/** Страница шаблона генератора: имя, как её назвали в Автокрафте («side-a», «←»); может быть пустым. */
export interface AutocraftGeneratorPage {
  name: string;
}

/**
 * Ответ `GET …/autocraft/generators/:hash/pages`: страницы шаблона без сборки макета.
 * `pages: null` — публичная страница генератора выключена, число страниц неизвестно.
 */
export interface AutocraftGeneratorPagesResult {
  pages: AutocraftGeneratorPage[] | null;
  /**
   * Страница каждого элемента шаблона по его хешу (1-based) — ключ поля формы и есть хеш
   * элемента. Схема формы страницу полю не сообщает. `null` — неизвестно.
   */
  fieldPages: Record<string, number> | null;
  /**
   * Поля, на которые ссылаются элементы-ссылки страницы (`template.data.linkTargetHash`):
   * ключ — номер страницы строкой, значение — хеши целевых полей. Ссылка рисует значение поля
   * из тех же данных, поэтому стороне с такой страницей нужно значение поля со стороны, где
   * оно заполняется. `null` — неизвестно.
   */
  linkTargetsByPage: Record<string, string[]> | null;
}

/** Что раскладке полей и ссылкам сторон нужно из страниц шаблона: страница поля и ссылки страниц. */
export type AutocraftTemplatePages = Pick<AutocraftGeneratorPagesResult, 'fieldPages' | 'linkTargetsByPage'>;

/** Прикреплённые к генератору данные: Excel/CSV или Google-таблица по ссылке. */
export interface AutocraftDataSource {
  hash: string;
  name: string;
  kind: LiteralUnion<'file' | 'google-sheet'>;
  /** У файла — когда обновился файл, у Google — когда сохранили ссылку. */
  updatedAt: string | null;
  /** Только у Google: когда таблицу прочитали для этого ответа. */
  fetchedAt?: string | null;
}

/**
 * Ответ `GET …/autocraft/generators/:hash/data`: строки прикреплённых данных генератора, уже
 * прогнанные через трансформер Автокрафта. Строкой заполняют форму точки — как список на
 * публичной странице генератора. `sources: []` — у генератора данных нет (или Автокрафт ещё
 * не умеет их отдавать): выбирать нечего.
 */
export interface AutocraftGeneratorDataResult {
  /** Источник, из которого собраны строки; `null` — данных нет. */
  source: AutocraftDataSource | null;
  /** Все источники генератора, без строк. */
  sources: AutocraftDataSource[];
  sheet: string | null;
  sheetNames: string[];
  /**
   * Строки — те же объекты, что принимает сборка PDF. Ключи — hash, имя или код поля (как в
   * таблице), поэтому в форму строка попадает через `mapAutocraftDataRow`.
   */
  dataItems: Record<string, string | number | boolean | null>[];
  /** Подпись каждой строки для списка, тот же порядок и длина, что у `dataItems`. */
  titles: string[];
}

export interface AutocraftDiscoverResult {
  generators: AutocraftGenerator[];
  config: { baseUrl: string; projectHash: string };
}

/**
 * Ответ `PUT …/autocraft/project-link`: общая ссылка карты сохранена. `bindingId` —
 * привязка к карте, по ней указатели «Навигации» собирают макеты; при замене ссылки он прежний.
 */
export interface AutocraftProjectLinkResult {
  bindingId: string;
  connectionId: string;
  generators: AutocraftGenerator[];
}

export interface AutocraftGenerationResult {
  pdfUrl: string;
  /** PNG-превью выбранной страницы макета (`previewPage` привязки); `null`, если PDF-сервис не отдал превью. */
  previewUrl: string | null;
  filename: string;
  /** Страниц в собранном PDF (заголовок `X-Pdf-Page-Count`); `null`, если Автокрафт его не прислал. */
  pageCount: number | null;
}

export interface AutocraftPreviewResult {
  /** PNG-превью выбранной страницы макета (`previewPage` привязки); `null`, если PDF-сервис не отдал превью. */
  previewUrl: string | null;
  /** Страниц в собранном PDF (заголовок `X-Pdf-Page-Count`); `null`, если Автокрафт его не прислал. */
  pageCount: number | null;
}
